import { all, put, takeLatest, call, select } from 'redux-saga/effects';
import { handleRequestError } from 'services/api/apiTools';
import { getParsingArrayToObj, getValidEmployee } from 'helpers/functions';
import ApiService from 'services/api';
import NotificationsService from 'services/notifications/NotificationsService';
import { PAGE_SIZE, VARIANT_UI } from 'constants/ui';
import {
  ERROR_TEXT_REUSE_EMAIL,
  HEADER_ATTENTION,
} from 'constants/notifications';
import { LOCATION_FILTER } from 'constants/location';
import { DEFAULT_SORT_CONFIG } from 'constants/sort';
import * as actions from './actions';
import * as authActions from '../auth/actions';
import * as notificationsActions from '../notifications/actions';

export function* getEmployeeListSaga(action) {
  const {
    isPagination,
    useDeleteToggle = false,
    defaultDeleteCustomersValue,
  } = action?.payload || {};

  const filterSearch = yield select((state) =>
    state?.employees?.fetchParams?.search?.trim()
  ) ?? '';
  const page = yield select(
    (state) => state?.employees?.fetchParams?.pagination?.page
  ) ?? 0;
  const size = yield select(
    (state) => state?.employees?.fetchParams?.pagination?.size
  ) ?? PAGE_SIZE;
  const location = yield select(
    (state) => state?.employees?.fetchParams?.location
  ) ?? LOCATION_FILTER.ALL;
  const sortConfig = yield select(
    (state) => state?.employees?.fetchParams?.sort
  ) ?? DEFAULT_SORT_CONFIG;
  const showDeletedEmployees = yield select(
    (state) => state?.employees?.fetchParams?.showDeletedEmployees
  ) ?? false;

  const currentShowDeleteValue = useDeleteToggle ? showDeletedEmployees : false;

  const showDelete =
    typeof defaultDeleteCustomersValue === 'boolean'
      ? defaultDeleteCustomersValue
      : currentShowDeleteValue;

  const filter = {
    filter: filterSearch,
    page: isPagination ? page : null,
    size: isPagination ? size : null,
    location: location && location !== LOCATION_FILTER.ALL ? location : null,
    sort_by: sortConfig.key,
    sort_order: sortConfig.direction,
    show_deleted: showDelete,
  };

  let requestValue;
  try {
    requestValue = yield call(ApiService.getEmployeeList, filter);
  } catch (errors) {
    handleRequestError(errors);
    yield put(actions.getEmployeeList.failure());
    return;
  }

  const validValue = getValidEmployee(requestValue.items);

  const employeeObj = getParsingArrayToObj(validValue);
  const employeeIds = Object.keys(employeeObj);

  yield put(
    actions.getEmployeeList.success({
      data: employeeObj,
      ids: employeeIds,
      page: requestValue.page,
      count: requestValue.count,
    })
  );
}

export function* createEmployeeSaga(action) {
  const { valueForm, onCloseModal, notifications } = action.payload;
  let requestValue;
  let requestValueEmployee;

  try {
    requestValue = yield call(ApiService.checkUserEmail, valueForm.login);
    const isUniqueEmail = requestValue.unique;

    if (!isUniqueEmail) {
      yield put(actions.createEmployee.failure());
      NotificationsService.throwNotification({
        title: HEADER_ATTENTION,
        text: ERROR_TEXT_REUSE_EMAIL,
        variant: VARIANT_UI.danger,
      });
      return;
    }

    requestValueEmployee = yield call(ApiService.createEmployee, valueForm);
  } catch (errors) {
    handleRequestError(errors);
    yield put(actions.createEmployee.failure());
    return;
  }

  if (notifications) {
    yield put(
      notificationsActions.updateEmployeeNotification.start({
        id: requestValueEmployee.id,
        data: notifications,
      })
    );
  }

  yield put(
    actions.getEmployeeList.start({ isPagination: true, useDeleteToggle: true })
  );

  yield put(actions.createEmployee.success());

  onCloseModal();
}

export function* changeEmployeeSaga(action) {
  const { data, login, isCurrentEmployee, notificationData } = action.payload;
  try {
    yield call(ApiService.changeEmployee, { data, login });
  } catch (errors) {
    handleRequestError(errors);
    yield put(actions.changeEmployee.failure());
    return;
  }

  yield put(
    actions.getEmployeeList.start({ isPagination: true, useDeleteToggle: true })
  );

  if (isCurrentEmployee) {
    yield put(authActions.getUserData.start());
  }

  if (notificationData) {
    yield put(
      notificationsActions.updateEmployeeNotification.start(notificationData)
    );
  }

  yield put(actions.changeEmployee.success());
}

export function* removeEmployeeSaga(action) {
  const params = action.payload;
  try {
    yield call(ApiService.removeEmployee, params);
  } catch (errors) {
    handleRequestError(errors);
    yield put(actions.removeEmployee.failure());
    return;
  }

  yield put(
    actions.getEmployeeList.start({ isPagination: true, useDeleteToggle: true })
  );
  yield put(actions.removeEmployee.success());
}

export function* getUserRolesSaga() {
  let requestValue;

  try {
    requestValue = yield call(ApiService.getUserRoles);
  } catch (errors) {
    handleRequestError(errors);
    yield put(actions.getUserRoles.failure());
    return;
  }

  yield put(actions.getUserRoles.success(requestValue));
}

export function* employeesSagas() {
  yield all([
    yield takeLatest(actions.getEmployeeList.start, getEmployeeListSaga),
    yield takeLatest(actions.createEmployee.start, createEmployeeSaga),
    yield takeLatest(actions.changeEmployee.start, changeEmployeeSaga),
    yield takeLatest(actions.removeEmployee.start, removeEmployeeSaga),
    yield takeLatest(actions.getUserRoles.start, getUserRolesSaga),
  ]);
}
